import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteApiMeUiPrefsSidebar, getApiMeUiPrefs, putApiMeUiPrefs, type UiPrefs } from '@cdf/api-client-react';
import { useCustomerSkin } from '@/components/tenancy/customer-brand-provider';

type Visibility = {
  active: boolean;
  visible: boolean;
  source: 'personal' | 'entity';
  pending: boolean;
  error: string | null;
  setVisible: (visible: boolean) => Promise<void>;
  resetToEntityDefault: () => Promise<void>;
};
const empty: Visibility = { active: false, visible: true, source: 'entity', pending: false, error: null,
  setVisible: async () => {}, resetToEntityDefault: async () => {} };
const Context = React.createContext<Visibility>(empty);

export function SidebarVisibilityProvider({ children }: { children: React.ReactNode }) {
  const { contextKey, activeEntitySlug, sidebarHiddenByDefault, sidebarSettingsReady } = useCustomerSkin();
  const client = useQueryClient();
  const key = React.useMemo(() => ['sidebar-visibility', contextKey], [contextKey]);
  const current = React.useRef(contextKey);
  current.current = contextKey;
  const [operation, setOperation] = React.useState<{ key: string; pending: boolean; error: string | null } | null>(null);
  const query = useQuery({
    queryKey: key,
    enabled: Boolean(activeEntitySlug),
    retry: false,
    queryFn: ({ signal }) => getApiMeUiPrefs({ headers: { 'X-Tenant-Slug': activeEntitySlug ?? '' }, signal, respectAbortSignal: true })
  });
  const pending = operation?.key === contextKey && operation.pending;
  async function update(hidden: boolean | null) {
    if (!activeEntitySlug || pending) return;
    const operationKey = contextKey;
    const request = { headers: { 'X-Tenant-Slug': activeEntitySlug }, assertContext: () => {
      if (current.current !== operationKey) throw new Error('Your entity changed.');
    } };
    setOperation({ key: operationKey, pending: true, error: null });
    await client.cancelQueries({ queryKey: key });
    const previous = client.getQueryData<UiPrefs>(key);
    client.setQueryData<UiPrefs>(key, { sidebarHidden: hidden });
    try {
      const result = hidden === null ? await deleteApiMeUiPrefsSidebar(request) : await putApiMeUiPrefs({ sidebarHidden: hidden }, request);
      if (current.current === operationKey) client.setQueryData(key, result);
    } catch {
      if (current.current === operationKey) {
        if (previous) client.setQueryData(key, previous);
        else client.removeQueries({ queryKey: key });
        setOperation({ key: operationKey, pending: false, error: 'Could not save your menu choice. Try again.' });
        return;
      }
    }
    if (current.current === operationKey) setOperation({ key: operationKey, pending: false, error: null });
  }
  const hidden = query.data?.sidebarHidden;
  const value: Visibility = {
    active: Boolean(activeEntitySlug),
    visible: sidebarSettingsReady && !(hidden ?? sidebarHiddenByDefault),
    source: hidden == null ? 'entity' : 'personal',
    pending,
    error: (operation?.key === contextKey ? operation.error : null) ?? (query.isError ? 'Could not load your menu choice. Try again later.' : null),
    setVisible: visible => update(!visible),
    resetToEntityDefault: () => update(null)
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useSidebarVisibility() { return React.useContext(Context); }
